import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivationsChart } from '@/components/app/activations-chart';
import { NeedsAttention } from '@/components/app/needs-attention';
import { RecentActivity } from '@/components/app/recent-activity';
import { apiFetch } from '@/lib/api';
import { relativeTime } from '@/lib/format';
import { useDateLocale } from '@/lib/date-locale';
import type { TFunction } from 'i18next';
import {
  dashboardStatsSchema,
  dashboardAlertsSchema,
  dashboardActivationsSchema,
  licensesAuditSchema,
} from '@/schemas/dashboard';
import type { DashboardStats, DashboardAlerts, AuditItem, ActivationItem } from '@/types/api';

async function fetchStats(): Promise<DashboardStats> {
  const resp = await apiFetch('/dashboard/stats');
  const json = await resp.json();
  return dashboardStatsSchema.parse(json);
}

async function fetchAlerts(): Promise<DashboardAlerts> {
  const resp = await apiFetch('/dashboard/alerts');
  const json = await resp.json();
  return dashboardAlertsSchema.parse(json);
}

async function fetchActivity(): Promise<{
  items: Array<AuditItem | ActivationItem>;
  isAudit: boolean;
}> {
  try {
    const resp = await apiFetch('/licenses/audit?limit=6');
    const json = await resp.json();
    const parsed = licensesAuditSchema.parse(json);
    return { items: parsed.data, isAudit: true };
  } catch {
    const resp = await apiFetch('/dashboard/activations?limit=6');
    const json = await resp.json();
    const parsed = dashboardActivationsSchema.parse(json);
    return { items: parsed.data, isAudit: false };
  }
}

const SECONDARY_STATS = [
  {
    key: 'expired' as const,
    labelKey: 'overview.statExpired',
    dotClass: 'bg-status-expired',
    route: '/licenses?status=expired',
    note: (stats: DashboardStats, t: TFunction) =>
      t('overview.inGracePeriod', { count: stats.grace }),
  },
  {
    key: 'revoked' as const,
    labelKey: 'overview.statRevoked',
    dotClass: 'bg-status-revoked',
    route: '/licenses?status=revoked',
    note: (stats: DashboardStats, t: TFunction) =>
      t('overview.percentOfFleet', {
        percent: Math.round((stats.revoked / Math.max(1, stats.total)) * 100),
      }),
  },
  {
    key: 'pending' as const,
    labelKey: 'overview.statPending',
    dotClass: 'bg-status-pending',
    route: '/licenses?status=pending',
    note: (_stats: DashboardStats, t: TFunction) => t('overview.awaitingReview'),
  },
];

export default function OverviewPage() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const statsQuery = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: fetchStats,
    staleTime: 30_000,
  });

  const alertsQuery = useQuery({
    queryKey: ['dashboard', 'alerts'],
    queryFn: fetchAlerts,
    staleTime: 30_000,
  });

  const activityQuery = useQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: fetchActivity,
    staleTime: 30_000,
  });

  const stats = statsQuery.data;
  const isEmpty = stats !== undefined && stats.total === 0;

  if (isEmpty) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4">
        <h1 className="text-3xl font-semibold tracking-tight">{t('overview.title')}</h1>
        <p className="text-muted-foreground">{t('overview.emptyTitle')}</p>
        <Button asChild>
          <Link to="/licenses/new">{t('overview.emptyCta')}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      {/* Page head */}
      <div className="flex items-start justify-between gap-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            {t('overview.title')}
          </h1>
          <p className="mt-2.5 max-w-[52ch] font-serif text-[17.5px] italic leading-relaxed text-muted-foreground">
            {t('overview.subtitleBase')}
            {stats
              ? ` — ${t('overview.subtitleActivations', { count: stats.activationsLast30Days })}${
                  alertsQuery.data
                    ? `, ${t('overview.subtitlePending', { count: alertsQuery.data.total })}`
                    : '.'
                }`
              : '.'}
          </p>
        </div>
        <Button asChild className="mt-2.5 shrink-0">
          <Link to="/licenses/new">{t('overview.newLicense')}</Link>
        </Button>
      </div>

      {/* Summary band */}
      {statsQuery.isLoading ? (
        <div className="mt-10 flex flex-col gap-8 border-y py-9 sm:flex-row sm:items-center sm:gap-11">
          <Skeleton className="h-24 w-40" />
          <Skeleton className="h-16 w-80" />
        </div>
      ) : statsQuery.isError ? (
        <div className="mt-10 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{t('overview.statsError')}</p>
          <button
            onClick={() => statsQuery.refetch()}
            className="mt-2 text-sm text-primary underline"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : stats ? (
        <section className="mt-10 flex flex-col gap-8 border-y py-9 sm:flex-row sm:items-center sm:gap-11">
          <Link
            to="/licenses?status=active"
            className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="text-[64px] font-semibold leading-none tracking-tight text-foreground">
              {stats.active}
            </div>
            <div className="mt-2.5 flex items-center gap-2 text-[14.5px] font-medium text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-status-active" aria-hidden="true" />
              {t('overview.activeLicenses')}
            </div>
          </Link>

          <div className="hidden w-px self-stretch bg-border sm:block" aria-hidden="true" />

          <div className="flex flex-wrap gap-x-9 gap-y-6">
            {SECONDARY_STATS.map((cfg) => (
              <Link
                key={cfg.key}
                to={cfg.route}
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="text-[26px] font-medium leading-none tracking-tight text-foreground">
                  {stats[cfg.key]}
                </div>
                <div className="mt-1.5 flex items-center gap-[7px] text-[13.5px] font-medium text-muted-foreground">
                  <span
                    className={`h-[7px] w-[7px] rounded-full ${cfg.dotClass}`}
                    aria-hidden="true"
                  />
                  {t(cfg.labelKey)}
                </div>
                <div className="mt-[3px] text-[12.5px] text-faint">{cfg.note(stats, t)}</div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* Activations */}
      <section className="mt-12">
        <ActivationsChart
          data={stats?.last30Days}
          total={stats?.activationsLast30Days}
          isLoading={statsQuery.isLoading}
          isError={statsQuery.isError}
          onRetry={() => statsQuery.refetch()}
        />
      </section>

      {/* Lower: attention + activity */}
      <section className="mt-12 grid items-start gap-12 lg:grid-cols-[7fr_5fr]">
        <NeedsAttention
          alerts={alertsQuery.data?.data ?? []}
          total={alertsQuery.data?.total ?? 0}
          isLoading={alertsQuery.isLoading}
          isError={alertsQuery.isError}
          onRetry={() => alertsQuery.refetch()}
        />
        <RecentActivity
          items={activityQuery.data?.items ?? []}
          isLoading={activityQuery.isLoading}
          isError={activityQuery.isError}
          onRetry={() => activityQuery.refetch()}
          isAudit={activityQuery.data?.isAudit ?? false}
        />
      </section>

      {/* Footer line */}
      {stats && (
        <footer className="mt-16 flex justify-between border-t pt-5 text-[13px] text-faint">
          <span>
            {t('overview.footer.licenses', { count: stats.total })} ·{' '}
            {t('overview.footer.activations', { count: stats.activationsTotal })}
          </span>
          <span>
            {t('overview.footer.synced', {
              time: relativeTime(statsQuery.dataUpdatedAt, dateLocale),
            })}
          </span>
        </footer>
      )}
    </div>
  );
}

export const handle = { crumb: 'Overview' };
